// Currency formatting utility for locale-specific number formatting
// Maps currency codes to their appropriate locale and formatting rules

// Currency code to locale mapping
const CURRENCY_LOCALE_MAP = {
  // Major currencies with specific locales
  USD: 'en-US', // United States Dollar
  EUR: 'de-DE', // Euro (using German formatting as default)
  GBP: 'en-GB', // British Pound
  JPY: 'ja-JP', // Japanese Yen
  CNY: 'zh-CN', // Chinese Yuan
  INR: 'en-IN', // Indian Rupee
  CAD: 'en-CA', // Canadian Dollar
  AUD: 'en-AU', // Australian Dollar
  CHF: 'de-CH', // Swiss Franc
  SEK: 'sv-SE', // Swedish Krona
  NOK: 'nb-NO', // Norwegian Krone
  DKK: 'da-DK', // Danish Krone
  KRW: 'ko-KR', // South Korean Won
  SGD: 'en-SG', // Singapore Dollar
  HKD: 'zh-HK', // Hong Kong Dollar
  NZD: 'en-NZ', // New Zealand Dollar
  MXN: 'es-MX', // Mexican Peso
  BRL: 'pt-BR', // Brazilian Real
  RUB: 'ru-RU', // Russian Ruble
  ZAR: 'en-ZA', // South African Rand
  TRY: 'tr-TR', // Turkish Lira
  PLN: 'pl-PL', // Polish Złoty
  CZK: 'cs-CZ', // Czech Koruna
  HUF: 'hu-HU', // Hungarian Forint
  RON: 'ro-RO', // Romanian Leu
  BGN: 'bg-BG', // Bulgarian Lev
  HRK: 'hr-HR', // Croatian Kuna
  ISK: 'is-IS', // Icelandic Króna

  // Asian currencies
  THB: 'th-TH', // Thai Baht
  VND: 'vi-VN', // Vietnamese Đồng
  IDR: 'id-ID', // Indonesian Rupiah
  MYR: 'ms-MY', // Malaysian Ringgit
  PHP: 'fil-PH', // Philippine Peso

  // Middle Eastern currencies
  AED: 'en-US', // UAE Dirham - hack: use en-US to avoid Arabic characters for better UI visibility
  SAR: 'en-US', // Saudi Riyal - hack: use en-US to avoid Arabic characters for better UI visibility
  QAR: 'en-US', // Qatari Riyal - hack: use en-US to avoid Arabic characters for better UI visibility
  KWD: 'en-US', // Kuwaiti Dinar - hack: use en-US to avoid Arabic characters for better UI visibility
  BHD: 'en-US', // Bahraini Dinar - hack: use en-US to avoid Arabic characters for better UI visibility
  OMR: 'en-US', // Omani Rial - hack: use en-US to avoid Arabic characters for better UI visibility
  JOD: 'en-US', // Jordanian Dinar - hack: use en-US to avoid Arabic characters for better UI visibility
  LBP: 'en-US', // Lebanese Pound - hack: use en-US to avoid Arabic characters for better UI visibility
  ILS: 'he-IL', // Israeli New Shekel

  // African currencies
  EGP: 'en-US', // Egyptian Pound - hack: use en-US to avoid Arabic characters for better UI visibility
  NGN: 'en-NG', // Nigerian Naira
  KES: 'sw-KE', // Kenyan Shilling
  GHS: 'en-GH', // Ghanaian Cedi

  // Latin American currencies
  ARS: 'es-AR', // Argentine Peso
  CLP: 'es-CL', // Chilean Peso
  COP: 'es-CO', // Colombian Peso
  PEN: 'es-PE', // Peruvian Sol
  UYU: 'es-UY', // Uruguayan Peso

  // Default fallback for other currencies
  DEFAULT: 'en-US',
};

// Extract currency code from currency string (e.g., "US Dollar (USD)" -> "USD")
const extractCurrencyCode = (currencyString) => {
  if (!currencyString) return null;

  // Match pattern like "Currency Name (CODE)"
  const match = currencyString.match(/\(([A-Z]{3})\)$/);
  return match ? match[1] : null;
};

// Get locale for a currency.
//
// Loose on the shape, because the two sources disagree: a currency SELECT
// supplies "US Dollar (USD)" from /compensations/currencies, while a currency
// custom field now carries a bare ISO code in its own options.
// Read strictly, the bare code matched nothing and fell through to the default
// locale -- so a field configured as INR grouped its money the US way and said
// nothing about it.
const getLocaleForCurrency = (currencyString) => {
  const currencyCode = resolveCurrencyCodeLoose(currencyString);
  return CURRENCY_LOCALE_MAP[currencyCode] || CURRENCY_LOCALE_MAP.DEFAULT;
};

// Resolve a currency code from a display string ("US Dollar (USD)") or a bare code ("USD").
const resolveCurrencyCodeLoose = (currencyString) => {
  if (!currencyString) return null;
  const str = currencyString.toString().trim();
  const parenMatch = str.match(/\(([A-Za-z]{3})\)\s*$/);
  if (parenMatch) return parenMatch[1].toUpperCase();
  return /^[A-Za-z]{3}$/.test(str) ? str.toUpperCase() : null;
};

// Locale + decimal/thousand separators for a currency (skipCurrencyExtraction reads a bare code).
const getCurrencySeparators = (currencyString, skipCurrencyExtraction) => {
  const locale = skipCurrencyExtraction
    ? CURRENCY_LOCALE_MAP[currencyString] || CURRENCY_LOCALE_MAP.DEFAULT
    : getLocaleForCurrency(currencyString);
  const decimalSep = (1.1).toLocaleString(locale).charAt(1);
  const thousandSep = decimalSep === '.' ? ',' : '.';
  return { locale, decimalSep, thousandSep };
};

// True when "." is the currency's decimal point (thousands are comma-grouped).
const currencySupportsDecimal = (currencyString) => {
  const code = resolveCurrencyCodeLoose(currencyString);
  const locale =
    (code && CURRENCY_LOCALE_MAP[code]) || CURRENCY_LOCALE_MAP.DEFAULT;
  return (1.1).toLocaleString(locale).charAt(1) === '.';
};

// Format an integer with the locale's thousand grouping (no fraction digits).
const formatGroupedInteger = (intValue, locale) => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(intValue);
  } catch {
    // Fallback to comma grouping if the locale is not supported.
    return String(intValue).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
};

// Format a value for display; with preserveDecimals, comma-separator currencies keep the typed fraction.
const formatCurrencyNumber = (
  value,
  currencyString,
  skipCurrencyExtraction = false,
  preserveDecimals = false
) => {
  if (!value || value === '') return '';

  const { locale, decimalSep, thousandSep } = getCurrencySeparators(
    currencyString,
    skipCurrencyExtraction
  );

  // Strip the thousand separator and normalise the decimal mark to ".".
  const cleaned = value
    .toString()
    .split(thousandSep)
    .join('')
    .replace(decimalSep, '.');

  // Group the integer part and re-attach the typed fraction (max 2 dp).
  if (preserveDecimals && decimalSep === '.') {
    const hasDot = cleaned.includes('.');
    const [intRaw, decRaw = ''] = cleaned.split('.');
    const intDigits = intRaw.replace(/\D/g, '');
    const decDigits = decRaw.replace(/\D/g, '').slice(0, 2);

    if (intDigits === '' && !hasDot) return '';

    const grouped = formatGroupedInteger(
      parseInt(intDigits || '0', 10),
      locale
    );
    return hasDot ? `${grouped}${decimalSep}${decDigits}` : grouped;
  }

  // Default (unchanged) integer-only behaviour.
  const parsedValue = parseFloat(cleaned);
  if (isNaN(parsedValue)) return '';
  return formatGroupedInteger(Math.floor(parsedValue), locale);
};

// Separators for a currency custom field, which has no currency selector: "." is
// the decimal point, as the backend stores it. In the selector's "Name (CODE)"
// form, the only form both the display and the cleaning path read.
export const CUSTOM_FIELD_CURRENCY = 'US Dollar (USD)';

// Clean input for storage: keep a single decimal (max 2 dp) for comma-separator currencies, digits-only otherwise.
export const cleanCurrencyInput = (value, currencyString) => {
  if (!value) return '';
  const str = value.toString();

  if (!currencyString || !currencySupportsDecimal(currencyString)) {
    return str.replace(/[^\d]/g, '');
  }

  const digitsAndDots = str.replace(/[^\d.]/g, '');
  const firstDot = digitsAndDots.indexOf('.');
  if (firstDot === -1) return digitsAndDots;

  const intPart = digitsAndDots.slice(0, firstDot).replace(/\./g, '') || '0';
  const decPart = digitsAndDots
    .slice(firstDot + 1)
    .replace(/\./g, '')
    .slice(0, 2);
  return `${intPart}.${decPart}`;
};

// Trim a stored/BE currency value to at most 2 dp, dropping insignificant
// trailing zeros: "50000.00" -> "50000", "26.50" -> "26.5". Used on reopen and
// on read surfaces so a saved decimal round-trips cleanly.
const normalizeCurrencyDecimals = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const [intPart, decPart = ''] = value.toString().split('.');
  const trimmed = decPart.slice(0, 2).replace(/0+$/, '');
  return trimmed ? `${intPart}.${trimmed}` : intPart;
};

export const currencyFormatterHelpers = {
  formatCurrencyNumber,
  cleanCurrencyInput,
  getLocaleForCurrency,
  extractCurrencyCode,
  currencySupportsDecimal,
  normalizeCurrencyDecimals,
};
